import { create } from 'zustand';
import brain from 'brain';
import { BusinessDetailsResponse } from 'types';
import { queryClient } from 'utils/queryClient';

interface BusinessStoreState {
  businessDetails: BusinessDetailsResponse | null;
  isLoading: boolean;
  error: string | null;
}

interface BusinessStoreActions {
  loadBusinessDetails: () => Promise<void>;
  clearBusinessDetails: () => void;
}

export const useBusinessStore = create<BusinessStoreState & BusinessStoreActions>((set, get) => ({
  // Initial state
  businessDetails: null,
  isLoading: false,
  error: null,

  // Actions
  loadBusinessDetails: async () => {
    const currentState = get();
    
    if (currentState.businessDetails) {
      return;
    }

    if (currentState.isLoading) {
      return;
    }

    // Use React Query cache if available
    const cached = queryClient.getQueryData<BusinessDetailsResponse>(['businessDetails']);
    if (cached) {
      set({ businessDetails: cached, isLoading: false, error: null });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const response = await brain.get_business_details();
      const data = await response.json();
      
      // Update both zustand store and React Query cache
      queryClient.setQueryData(['businessDetails'], data);
      set({ businessDetails: data, isLoading: false });
    } catch (error) {
      set({ 
        businessDetails: null, 
        isLoading: false, 
        error: 'Failed to load business details' 
      });
    }
  },

  clearBusinessDetails: () => {
    set({ 
      businessDetails: null, 
      isLoading: false, 
      error: null 
    });
  },
}));
